import React, { useState, useEffect, useMemo } from 'react';
import { Cloud, Plus, Settings, Trash2, Play, Eye, AlertTriangle, CheckCircle, Clock, BarChart3, Shield, X, FileCode, GitBranch, ChevronDown, ChevronRight, ExternalLink, AlertCircle, Info, Filter, SortDesc, Layers, Database, Lock, Code, Download, FileText, CheckSquare, Square, Server, Workflow, FileJson } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const AzureDevOpsScanner = ({ api, user, onNavigateToProfile }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [azureOrgs, setAzureOrgs] = useState([]);
  const [scanResults, setScanResults] = useState([]);
  const [scanJobs, setScanJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState('');

  // Scan detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [scanDetail, setScanDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [expandedRepos, setExpandedRepos] = useState({});

  // Filtering and sorting state for detail modal
  const [detailView, setDetailView] = useState('all'); // 'all', 'mcp', 'pii', 'compliance', 'security', 'repos'
  const [detailFilter, setDetailFilter] = useState([]);
  const [detailSort, setDetailSort] = useState('confidence-desc');

  // Pagination state
  const [complianceLimit, setComplianceLimit] = useState(50);
  const [securityLimit, setSecurityLimit] = useState(50);

  // Export state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportSelections, setExportSelections] = useState({
    repos: {},
    detections: {},
    compliance: {},
    security: {}
  });
  const [selectAllRepos, setSelectAllRepos] = useState(true);

  // Load data on mount
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadAzureOrganizations(),
        loadScanJobs(),
        loadScanResults()
      ]);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAzureOrganizations = async () => {
    try {
      const response = await api.getAzureDevOpsOrganizations();
      setAzureOrgs(response.organizations || []);
    } catch (error) {
      console.error('Error loading Azure DevOps organizations:', error);
      setAzureOrgs([]);
    }
  };

  const loadScanJobs = async () => {
    try {
      const response = await api.getAzureDevOpsScanJobs();
      setScanJobs(response.jobs || []);
    } catch (error) {
      console.error('Error loading scan jobs:', error);
      setScanJobs([]);
    }
  };

  const loadScanResults = async (organization = null) => {
    try {
      if (organization || selectedOrg) {
        const response = await api.getAzureDevOpsScanResults(organization || selectedOrg);
        setScanResults(response.scans || []);
      }
    } catch (error) {
      console.error('Error loading scan results:', error);
      setScanResults([]);
    }
  };

  const handleManualScan = async (orgUrl) => {
    try {
      setLoading(true);
      const orgName = extractOrgNameFromUrl(orgUrl);
      const response = await api.triggerAzureDevOpsScan(orgName);

      if (response.job_id) {
        pollForScanCompletion(response.job_id, orgName);
      }

      setTimeout(() => loadScanJobs(), 500);
    } catch (error) {
      alert(`Error triggering scan: ${error.message}`);
      setLoading(false);
    }
  };

  const pollForScanCompletion = (jobId, orgName) => {
    let pollCount = 0;
    const maxPolls = 60;

    const pollInterval = setInterval(async () => {
      pollCount++;

      try {
        const response = await api.getAzureDevOpsScanJobs(null, 5);
        const job = response.jobs?.find(j => j.job_id === jobId);

        if (job) {
          if (job.status === 'completed') {
            clearInterval(pollInterval);
            setLoading(false);

            await loadScanJobs();
            await loadScanResults(orgName);

            const detections = job.results?.mcp_detections_count || 0;
            const securityFindings = job.results?.azure_security_findings_count || 0;
            const repos = job.results?.repositories_scanned || 0;
            alert(`Scan completed!\n\n${repos} repositories scanned\n${detections} MCP detections found\n${securityFindings} Azure security issues found`);

            if (job.results?.scan_id && (detections > 0 || securityFindings > 0)) {
              openDetailModal(job.results.scan_id);
            }
          } else if (job.status === 'failed') {
            clearInterval(pollInterval);
            setLoading(false);
            alert(`Scan failed: ${job.error || 'Unknown error'}`);
            await loadScanJobs();
          }
        }

        if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
          setLoading(false);
          await loadScanJobs();
        }
      } catch (error) {
        console.error('Error polling scan status:', error);
      }
    }, 2000);
  };

  const extractOrgNameFromUrl = (url) => {
    if (url.includes('dev.azure.com/')) {
      const parts = url.replace(/\/$/, '').split('dev.azure.com/');
      if (parts.length > 1) {
        return parts[1].split('/')[0];
      }
    }
    if (url.includes('.visualstudio.com')) {
      const match = url.match(/https?:\/\/([^.]+)\.visualstudio\.com/);
      if (match) return match[1];
    }
    return url;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} className="status-icon status-completed" />;
      case 'failed':
        return <AlertTriangle size={16} className="status-icon status-failed" />;
      case 'running':
        return <Clock size={16} className="status-icon status-running" />;
      default:
        return <Clock size={16} className="status-icon status-pending" />;
    }
  };

  const loadScanDetail = async (scanId) => {
    try {
      setLoadingDetail(true);
      const detail = await api.getAzureDevOpsScanDetail(scanId);
      setScanDetail(detail);
      setShowDetailModal(true);
      setExpandedRepos({});
    } catch (error) {
      console.error('Error loading scan detail:', error);
      alert(`Error loading scan details: ${error.message}`);
    } finally {
      setLoadingDetail(false);
    }
  };

  const toggleRepoExpansion = (repoName) => {
    setExpandedRepos(prev => ({
      ...prev,
      [repoName]: !prev[repoName]
    }));
  };

  const getConfidenceBadge = (confidence) => {
    if (confidence >= 0.7) return { className: 'confidence-high', label: 'High' };
    if (confidence >= 0.4) return { className: 'confidence-medium', label: 'Medium' };
    return { className: 'confidence-low', label: 'Low' };
  };

  const getSeverityBadge = (severity) => {
    const severityMap = {
      critical: { className: 'severity-critical', label: 'Critical' },
      high: { className: 'severity-high', label: 'High' },
      medium: { className: 'severity-medium', label: 'Medium' },
      low: { className: 'severity-low', label: 'Low' }
    };
    return severityMap[severity] || { className: 'severity-low', label: severity };
  };

  // Get all detections flattened from repositories
  const getAllDetections = useMemo(() => {
    if (!scanDetail?.repositories) return [];
    const allDetections = [];
    scanDetail.repositories.forEach(repo => {
      (repo.mcp_detections || []).forEach(d => {
        allDetections.push({ ...d, repository: repo.full_name, project: repo.project, category: 'mcp' });
      });
    });
    return allDetections;
  }, [scanDetail]);

  // Get all Azure security findings flattened
  const getAllSecurityFindings = useMemo(() => {
    if (!scanDetail?.repositories) return [];
    const allFindings = [];
    scanDetail.repositories.forEach(repo => {
      (repo.azure_security_findings || []).forEach(f => {
        allFindings.push({ ...f, repository: repo.full_name, project: repo.project, category: 'security' });
      });
    });
    return allFindings;
  }, [scanDetail]);

  // Get all compliance findings flattened
  const getAllCompliance = useMemo(() => {
    if (!scanDetail?.repositories) return [];
    const allFindings = [];
    scanDetail.repositories.forEach(repo => {
      (repo.compliance_findings || []).forEach(f => {
        allFindings.push({ ...f, repository: repo.full_name, project: repo.project, category: 'compliance' });
      });
    });
    return allFindings;
  }, [scanDetail]);

  const openDetailModal = async (scanId) => {
    setDetailView('all');
    setDetailFilter([]);
    setDetailSort('confidence-desc');
    setExpandedRepos({});
    setComplianceLimit(50);
    setSecurityLimit(50);
    setShowExportModal(false);
    await loadScanDetail(scanId);
  };

  // Generate PDF report
  const generatePDFReport = () => {
    if (!scanDetail) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPos = 20;

    // Title
    doc.setFontSize(20);
    doc.setTextColor(0, 120, 212); // Azure blue
    doc.text('Azure DevOps Security Scan Report', pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Organization and date
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Organization: ${scanDetail.organization}`, 20, yPos);
    yPos += 8;
    doc.text(`Scan Date: ${new Date(scanDetail.started_at).toLocaleString()}`, 20, yPos);
    yPos += 8;
    doc.text(`Generated: ${new Date().toLocaleString()}`, 20, yPos);
    yPos += 15;

    // Summary
    doc.setFontSize(14);
    doc.setTextColor(0, 120, 212);
    doc.text('Summary', 20, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Projects Scanned: ${scanDetail.projects_scanned}`, 20, yPos);
    yPos += 6;
    doc.text(`Repositories Scanned: ${scanDetail.repositories_scanned}`, 20, yPos);
    yPos += 6;
    doc.text(`Pipelines Scanned: ${scanDetail.pipelines_scanned}`, 20, yPos);
    yPos += 6;
    doc.text(`MCP Detections: ${scanDetail.total_mcp_detections}`, 20, yPos);
    yPos += 6;
    doc.text(`Azure Security Findings: ${scanDetail.total_azure_security_findings}`, 20, yPos);
    yPos += 6;
    doc.text(`Compliance Findings: ${scanDetail.total_compliance_findings}`, 20, yPos);
    yPos += 15;

    // Azure Security Findings Table
    const securityData = [];
    getAllSecurityFindings.forEach(f => {
      securityData.push([
        f.repository?.split('/')[1] || f.project,
        f.type?.replace(/_/g, ' ') || 'Unknown',
        f.severity || 'medium',
        f.resource_type || 'Unknown',
        f.remediation?.substring(0, 60) + '...' || 'N/A'
      ]);
    });

    if (securityData.length > 0) {
      if (yPos > 200) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.setTextColor(0, 120, 212);
      doc.text('Azure Security Findings', 20, yPos);
      yPos += 5;

      doc.autoTable({
        startY: yPos,
        head: [['Repository', 'Type', 'Severity', 'Resource', 'Remediation']],
        body: securityData.slice(0, 50),
        theme: 'striped',
        headStyles: { fillColor: [0, 120, 212] },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: 35 },
          2: { cellWidth: 20 },
          3: { cellWidth: 25 },
          4: { cellWidth: 55 }
        }
      });
      yPos = doc.lastAutoTable.finalY + 15;
    }

    // MCP Detections Table
    const detectionsData = [];
    getAllDetections.forEach(d => {
      detectionsData.push([
        d.repository?.split('/')[1] || d.project,
        d.type?.replace(/_/g, ' '),
        d.file_path || 'N/A',
        `${Math.round(d.confidence * 100)}%`,
        d.confidence >= 0.7 ? 'High' : d.confidence >= 0.4 ? 'Medium' : 'Low'
      ]);
    });

    if (detectionsData.length > 0) {
      if (yPos > 200) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFontSize(14);
      doc.setTextColor(0, 120, 212);
      doc.text('MCP Detections', 20, yPos);
      yPos += 5;

      doc.autoTable({
        startY: yPos,
        head: [['Repository', 'Type', 'File', 'Confidence', 'Severity']],
        body: detectionsData.slice(0, 50),
        theme: 'striped',
        headStyles: { fillColor: [0, 120, 212] },
        styles: { fontSize: 8, cellPadding: 2 }
      });
    }

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Agentic Threat Modeler - Azure DevOps - Page ${i} of ${pageCount}`,
        pageWidth / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    const filename = `azure-devops-scan-${scanDetail.organization}-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
    setShowExportModal(false);
  };

  const renderDetailModal = () => {
    if (!showDetailModal || !scanDetail) return null;

    return (
      <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
        <div className="scan-detail-modal azure-detail-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title">
              <Cloud size={24} className="azure-icon" />
              <div>
                <h2>Scan Results: {scanDetail.organization}</h2>
                <span className="scan-date">
                  {new Date(scanDetail.started_at).toLocaleString()}
                </span>
              </div>
            </div>
            <div className="modal-header-actions">
              <button className="export-btn" onClick={generatePDFReport}>
                <Download size={18} />
                Export PDF
              </button>
              <button className="modal-close" onClick={() => setShowDetailModal(false)}>
                <X size={24} />
              </button>
            </div>
          </div>

          {/* Summary Statistics */}
          <div className="detail-summary clickable-summary azure-summary">
            <div
              className={`summary-card clickable ${detailView === 'repos' ? 'active' : ''}`}
              onClick={() => setDetailView('repos')}
            >
              <div className="summary-icon repos">
                <Server size={20} />
              </div>
              <div className="summary-content">
                <div className="summary-value">{scanDetail.projects_scanned}</div>
                <div className="summary-label">Projects</div>
              </div>
            </div>
            <div
              className={`summary-card clickable ${detailView === 'mcp' ? 'active' : ''}`}
              onClick={() => setDetailView('mcp')}
            >
              <div className="summary-icon detections">
                <Code size={20} />
              </div>
              <div className="summary-content">
                <div className="summary-value">{scanDetail.total_mcp_detections}</div>
                <div className="summary-label">MCP Detections</div>
              </div>
            </div>
            <div
              className={`summary-card clickable ${detailView === 'security' ? 'active' : ''}`}
              onClick={() => setDetailView('security')}
            >
              <div className="summary-icon security">
                <AlertTriangle size={20} />
              </div>
              <div className="summary-content">
                <div className="summary-value">{scanDetail.total_azure_security_findings}</div>
                <div className="summary-label">Security Issues</div>
              </div>
            </div>
            <div
              className={`summary-card clickable ${detailView === 'compliance' ? 'active' : ''}`}
              onClick={() => setDetailView('compliance')}
            >
              <div className="summary-icon compliance">
                <Shield size={20} />
              </div>
              <div className="summary-content">
                <div className="summary-value">{scanDetail.total_compliance_findings}</div>
                <div className="summary-label">Compliance</div>
              </div>
            </div>
          </div>

          {/* View Tabs */}
          <div className="detail-controls">
            <div className="view-tabs">
              <button
                className={`view-tab ${detailView === 'all' ? 'active' : ''}`}
                onClick={() => setDetailView('all')}
              >
                <Layers size={16} /> All
              </button>
              <button
                className={`view-tab ${detailView === 'security' ? 'active' : ''}`}
                onClick={() => setDetailView('security')}
              >
                <AlertTriangle size={16} /> Azure Security
              </button>
              <button
                className={`view-tab ${detailView === 'repos' ? 'active' : ''}`}
                onClick={() => setDetailView('repos')}
              >
                <Database size={16} /> By Project
              </button>
            </div>

            <div className="sort-controls">
              <label><SortDesc size={14} /> Sort:</label>
              <select
                value={detailSort}
                onChange={(e) => setDetailSort(e.target.value)}
              >
                <option value="confidence-desc">Severity (High to Low)</option>
                <option value="confidence-asc">Severity (Low to High)</option>
                <option value="type">Detection Type</option>
                <option value="repo">Repository</option>
              </select>
            </div>
          </div>

          {/* Content */}
          <div className="detail-content">
            {/* All View */}
            {detailView === 'all' && (
              <div className="all-detections-view">
                <h3>All Findings ({getAllDetections.length + getAllSecurityFindings.length + getAllCompliance.length})</h3>

                {getAllSecurityFindings.length > 0 && (
                  <div className="section-group">
                    <h4><AlertTriangle size={18} /> Azure Security Findings ({getAllSecurityFindings.length})</h4>
                    <div className="security-list">
                      {getAllSecurityFindings.slice(0, 10).map((finding, idx) => (
                        <div key={idx} className={`security-item severity-${finding.severity}`}>
                          <div className="security-header">
                            <span className="security-type">{finding.type?.replace(/_/g, ' ')}</span>
                            <span className={`severity-badge ${getSeverityBadge(finding.severity).className}`}>
                              {getSeverityBadge(finding.severity).label}
                            </span>
                          </div>
                          <div className="security-meta">
                            <span><Server size={14} /> {finding.project}</span>
                            <span><FileJson size={14} /> {finding.resource_type}</span>
                          </div>
                          <div className="security-remediation">
                            <strong>Remediation:</strong> {finding.remediation}
                          </div>
                        </div>
                      ))}
                      {getAllSecurityFindings.length > 10 && (
                        <button className="show-more-btn" onClick={() => setDetailView('security')}>
                          View all {getAllSecurityFindings.length} security findings
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {getAllDetections.length > 0 && (
                  <div className="section-group">
                    <h4><Code size={18} /> MCP Detections ({getAllDetections.length})</h4>
                    <div className="detection-list">
                      {getAllDetections.slice(0, 10).map((detection, idx) => (
                        <div key={idx} className="detection-item">
                          <div className="detection-header">
                            <span className="detection-type">{detection.type?.replace(/_/g, ' ')}</span>
                            <span className={`confidence-badge ${getConfidenceBadge(detection.confidence).className}`}>
                              {getConfidenceBadge(detection.confidence).label} ({Math.round(detection.confidence * 100)}%)
                            </span>
                          </div>
                          <div className="detection-meta">
                            <span><Server size={14} /> {detection.project}</span>
                            <span><FileCode size={14} /> {detection.file_path}</span>
                          </div>
                        </div>
                      ))}
                      {getAllDetections.length > 10 && (
                        <button className="show-more-btn" onClick={() => setDetailView('mcp')}>
                          View all {getAllDetections.length} detections
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Security View */}
            {detailView === 'security' && (
              <div className="security-list-view">
                <h3>Azure Security Findings ({getAllSecurityFindings.length})</h3>
                {getAllSecurityFindings.length === 0 ? (
                  <div className="no-results">
                    <CheckCircle size={24} />
                    <p>No Azure security misconfigurations found.</p>
                  </div>
                ) : (
                  <div className="security-list">
                    {getAllSecurityFindings.slice(0, securityLimit).map((finding, idx) => (
                      <div key={idx} className={`security-item severity-${finding.severity}`}>
                        <div className="security-header">
                          <span className="security-type">{finding.type?.replace(/_/g, ' ')}</span>
                          <span className={`severity-badge ${getSeverityBadge(finding.severity).className}`}>
                            {getSeverityBadge(finding.severity).label}
                          </span>
                        </div>
                        <div className="security-meta">
                          <span><Server size={14} /> {finding.project}</span>
                          <span><FileJson size={14} /> {finding.resource_type}</span>
                          <span><FileCode size={14} /> {finding.file_path}</span>
                        </div>
                        {finding.evidence && finding.evidence.length > 0 && (
                          <div className="security-evidence">
                            <pre className="evidence-code">
                              {finding.evidence.slice(0, 3).map((line, i) => (
                                <div key={i} className="evidence-line">{line}</div>
                              ))}
                            </pre>
                          </div>
                        )}
                        <div className="security-remediation">
                          <strong>Remediation:</strong> {finding.remediation}
                        </div>
                      </div>
                    ))}
                    {getAllSecurityFindings.length > securityLimit && (
                      <div className="pagination-controls">
                        <button
                          className="show-more-btn"
                          onClick={() => setSecurityLimit(prev => prev + 50)}
                        >
                          Show More (+50)
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* MCP View */}
            {detailView === 'mcp' && (
              <div className="detections-list-view">
                <h3>MCP Detections ({getAllDetections.length})</h3>
                {getAllDetections.length === 0 ? (
                  <div className="no-results">
                    <Info size={24} />
                    <p>No MCP detections found.</p>
                  </div>
                ) : (
                  <div className="detection-list">
                    {getAllDetections.map((detection, idx) => (
                      <div key={idx} className="detection-item">
                        <div className="detection-header">
                          <span className="detection-type">{detection.type?.replace(/_/g, ' ')}</span>
                          <span className={`confidence-badge ${getConfidenceBadge(detection.confidence).className}`}>
                            {getConfidenceBadge(detection.confidence).label} ({Math.round(detection.confidence * 100)}%)
                          </span>
                        </div>
                        <div className="detection-meta">
                          <span className="detection-repo"><Server size={14} /> {detection.project}</span>
                          <span className="detection-file"><FileCode size={14} /> {detection.file_path}</span>
                        </div>
                        {detection.evidence && detection.evidence.length > 0 && (
                          <div className="detection-evidence">
                            <pre className="evidence-code">
                              {detection.evidence.slice(0, 3).map((line, i) => (
                                <div key={i} className="evidence-line">{line}</div>
                              ))}
                            </pre>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* By Project/Repo View */}
            {detailView === 'repos' && (
              <div className="repositories-section">
                <h3>By Project ({scanDetail.repositories?.length || 0})</h3>
                <div className="repo-list">
                  {(scanDetail.repositories || []).map((repo) => (
                    <div key={repo.full_name} className="repo-card">
                      <div
                        className="repo-header"
                        onClick={() => toggleRepoExpansion(repo.full_name)}
                      >
                        <div className="repo-expand-icon">
                          {expandedRepos[repo.full_name] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                        </div>
                        <div className="repo-info">
                          <div className="repo-name">
                            <Server size={16} />
                            {repo.project} / {repo.name}
                          </div>
                          <div className="repo-full-name">{repo.full_name}</div>
                        </div>
                        <div className="repo-badges">
                          {repo.mcp_detections_count > 0 && (
                            <span className="badge detection-badge">
                              <Code size={14} />
                              {repo.mcp_detections_count} MCP
                            </span>
                          )}
                          {repo.azure_security_findings_count > 0 && (
                            <span className="badge security-badge">
                              <AlertTriangle size={14} />
                              {repo.azure_security_findings_count} Security
                            </span>
                          )}
                          {repo.compliance_findings_count > 0 && (
                            <span className="badge compliance-badge">
                              <Shield size={14} />
                              {repo.compliance_findings_count} Compliance
                            </span>
                          )}
                        </div>
                      </div>

                      {expandedRepos[repo.full_name] && (
                        <div className="repo-details">
                          {repo.azure_security_findings && repo.azure_security_findings.length > 0 && (
                            <div className="security-section">
                              <h5><AlertTriangle size={16} /> Security Findings</h5>
                              {repo.azure_security_findings.map((finding, idx) => (
                                <div key={idx} className={`security-item compact severity-${finding.severity}`}>
                                  <div className="security-header">
                                    <span className="security-type">{finding.type?.replace(/_/g, ' ')}</span>
                                    <span className={`severity-badge ${getSeverityBadge(finding.severity).className}`}>
                                      {getSeverityBadge(finding.severity).label}
                                    </span>
                                  </div>
                                  <div className="security-remediation">
                                    {finding.remediation}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          {repo.mcp_detections && repo.mcp_detections.length > 0 && (
                            <div className="detection-section">
                              <h5><Code size={16} /> MCP Detections</h5>
                              {repo.mcp_detections.map((detection, idx) => (
                                <div key={idx} className="detection-item">
                                  <div className="detection-header">
                                    <span className="detection-type">{detection.type?.replace(/_/g, ' ')}</span>
                                    <span className={`confidence-badge ${getConfidenceBadge(detection.confidence).className}`}>
                                      {Math.round(detection.confidence * 100)}%
                                    </span>
                                  </div>
                                  <div className="detection-file">
                                    <FileCode size={14} />
                                    <span>{detection.file_path}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Compliance View */}
            {detailView === 'compliance' && (
              <div className="compliance-list-view">
                <h3>ISO 42001 Compliance Findings ({getAllCompliance.length})</h3>
                {getAllCompliance.length === 0 ? (
                  <div className="no-results">
                    <Shield size={24} />
                    <p>No compliance findings in this scan.</p>
                  </div>
                ) : (
                  <div className="compliance-list">
                    {getAllCompliance.slice(0, complianceLimit).map((finding, idx) => (
                      <div key={idx} className="finding-item">
                        <div className="finding-header">
                          <span className="finding-control">{finding.control_id}</span>
                          <span className="finding-type">{finding.type?.replace(/_/g, ' ')}</span>
                          <span className={`confidence-badge ${getConfidenceBadge(finding.confidence).className}`}>
                            {Math.round(finding.confidence * 100)}%
                          </span>
                        </div>
                        <div className="finding-meta">
                          <span><Server size={14} /> {finding.project}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderDashboard = () => (
    <div className="azure-devops-scanner-dashboard">
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <Cloud size={32} className="azure-icon" />
          <div>
            <h2>Azure DevOps Organization Scanner</h2>
            <p>MCP Detection, ISO 42001 Compliance & Azure Security Scanning</p>
          </div>
        </div>
        <button
          className="azure-scanner-btn primary"
          onClick={() => onNavigateToProfile('azuredevops')}
        >
          <Plus size={20} />
          Manage Organizations
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="dashboard-stats">
        <div className="stat-card azure-stat">
          <div className="stat-icon">
            <Cloud size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{azureOrgs.length}</div>
            <div className="stat-label">Azure DevOps Organizations</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <AlertTriangle size={24} className="security-icon" />
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {scanResults.reduce((sum, result) => sum + (result.azure_security_findings_count || 0), 0)}
            </div>
            <div className="stat-label">Security Findings</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <Code size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {scanResults.reduce((sum, result) => sum + result.mcp_detections_count, 0)}
            </div>
            <div className="stat-label">MCP Detections</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">
              {scanJobs.filter(job => job.status === 'completed').length}
            </div>
            <div className="stat-label">Completed Scans</div>
          </div>
        </div>
      </div>

      {/* Azure DevOps Organizations */}
      <div className="dashboard-section">
        <div className="section-header">
          <h3>Azure DevOps Organizations</h3>
          <button
            className="azure-scanner-btn secondary"
            onClick={() => onNavigateToProfile('azuredevops')}
          >
            <Settings size={16} />
            Manage Organizations
          </button>
        </div>
        <div className="scan-configs">
          {azureOrgs.length === 0 ? (
            <div className="empty-state">
              <Cloud size={48} className="azure-icon-empty" />
              <h4>No Azure DevOps Organizations Configured</h4>
              <p>Add your first Azure DevOps organization to start scanning for MCP development, compliance issues, and Azure security misconfigurations.</p>
              <button
                className="azure-scanner-btn primary"
                onClick={() => onNavigateToProfile('azuredevops')}
              >
                <Plus size={20} />
                Add Organization
              </button>
            </div>
          ) : (
            <div className="config-grid">
              {azureOrgs.map((org) => (
                <div key={org.id} className="config-card azure-config-card">
                  <div className="config-header">
                    <h4>{org.name}</h4>
                    <div className="config-actions">
                      <button
                        className="action-btn"
                        onClick={() => handleManualScan(org.organization_url)}
                        title="Run manual scan"
                      >
                        <Play size={16} />
                      </button>
                      <button
                        className="action-btn"
                        onClick={() => {
                          setSelectedOrg(extractOrgNameFromUrl(org.organization_url));
                          setActiveTab('results');
                          loadScanResults(extractOrgNameFromUrl(org.organization_url));
                        }}
                        title="View results"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        className="action-btn"
                        onClick={() => onNavigateToProfile('azuredevops')}
                        title="Manage organization"
                      >
                        <Settings size={16} />
                      </button>
                    </div>
                  </div>
                  <div className="config-details">
                    <div className="config-detail">
                      <span className="label">URL:</span>
                      <span className="value org-url" title={org.organization_url}>
                        {org.organization_url.length > 40 ? `${org.organization_url.substring(0, 37)}...` : org.organization_url}
                      </span>
                    </div>
                    <div className="config-detail">
                      <span className="label">Frequency:</span>
                      <span className="value">{org.scan_frequency}</span>
                    </div>
                    <div className="config-detail">
                      <span className="label">Max Projects:</span>
                      <span className="value">{org.max_projects}</span>
                    </div>
                    <div className="config-detail">
                      <span className="label">Last Scan:</span>
                      <span className="value">
                        {org.last_scan_at
                          ? new Date(org.last_scan_at).toLocaleDateString()
                          : 'Never'
                        }
                      </span>
                    </div>
                  </div>
                  <div className="config-scan-options">
                    {org.scan_pipelines && <span className="scan-option-badge">Pipelines</span>}
                    {org.scan_arm_templates && <span className="scan-option-badge">ARM</span>}
                    {org.scan_bicep && <span className="scan-option-badge">Bicep</span>}
                  </div>
                  <div className={`config-status ${org.is_active ? 'active' : 'inactive'}`}>
                    {org.is_active ? 'Active' : 'Inactive'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent Jobs */}
      <div className="dashboard-section">
        <h3>Recent Scan Jobs</h3>
        <div className="job-list">
          {scanJobs.slice(0, 5).map((job) => (
            <div
              key={job.job_id || job.organization + job.created_at}
              className={`job-item ${job.status === 'completed' && job.results?.scan_id ? 'clickable' : ''}`}
              onClick={() => {
                if (job.status === 'completed' && job.results?.scan_id) {
                  openDetailModal(job.results.scan_id);
                }
              }}
              title={job.status === 'completed' && job.results?.scan_id ? 'Click to view details' : ''}
            >
              <div className="job-status">
                {getStatusIcon(job.status)}
              </div>
              <div className="job-info">
                <div className="job-org">{job.organization}</div>
                <div className="job-details">
                  <span>Started: {new Date(job.created_at).toLocaleString()}</span>
                  {job.results?.mcp_detections_count > 0 && (
                    <span className="detection-count">{job.results.mcp_detections_count} MCP</span>
                  )}
                  {job.results?.azure_security_findings_count > 0 && (
                    <span className="security-count">{job.results.azure_security_findings_count} security</span>
                  )}
                </div>
              </div>
              <div className="job-actions">
                {job.error && (
                  <div className="job-error" title={job.error}>
                    <AlertTriangle size={16} />
                  </div>
                )}
                {job.status === 'completed' && job.results?.scan_id && (
                  <div className="view-hint">
                    <Eye size={14} />
                  </div>
                )}
              </div>
            </div>
          ))}
          {scanJobs.length === 0 && (
            <div className="empty-state-small">
              <p>No scan jobs yet. Configure an organization to start scanning.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderResults = () => (
    <div className="azure-devops-scanner-results">
      <div className="results-header">
        <h3>Scan Results</h3>
        <div className="org-selector">
          <select
            value={selectedOrg}
            onChange={(e) => {
              setSelectedOrg(e.target.value);
              loadScanResults(e.target.value);
            }}
          >
            <option value="">Select Organization</option>
            {azureOrgs.map((org) => (
              <option key={org.id} value={extractOrgNameFromUrl(org.organization_url)}>
                {org.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedOrg && (
        <div className="results-content">
          {scanResults.length === 0 ? (
            <div className="empty-state">
              <BarChart3 size={48} />
              <h4>No Scan Results</h4>
              <p>No scans have been completed for {selectedOrg} yet.</p>
            </div>
          ) : (
            <div className="results-grid">
              {scanResults.map((result) => (
                <div
                  key={result.scan_id}
                  className="result-card clickable"
                  onClick={() => openDetailModal(result.scan_id)}
                  title="Click to view detailed results"
                >
                  <div className="result-header">
                    <h4>{result.organization}</h4>
                    <div className="result-date">
                      {new Date(result.started_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="result-stats">
                    <div className="result-stat">
                      <span className="stat-label">Projects:</span>
                      <span className="stat-value">{result.projects_scanned}</span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">Repositories:</span>
                      <span className="stat-value">{result.repositories_scanned}</span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">Pipelines:</span>
                      <span className="stat-value">{result.pipelines_scanned}</span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">Security Issues:</span>
                      <span className="stat-value highlight-security">{result.azure_security_findings_count}</span>
                    </div>
                    <div className="result-stat">
                      <span className="stat-label">MCP Detections:</span>
                      <span className="stat-value highlight-detection">{result.mcp_detections_count}</span>
                    </div>
                  </div>
                  <div className="result-footer">
                    <div className={`result-status ${result.scan_status}`}>
                      {result.scan_status}
                    </div>
                    <div className="view-details-hint">
                      <Eye size={14} /> Click to view details
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="azure-devops-scanner">
      {/* Tab Navigation */}
      <div className="scanner-tabs">
        <button
          className={`scanner-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          <Settings size={20} />
          Dashboard
        </button>
        <button
          className={`scanner-tab ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => setActiveTab('results')}
        >
          <BarChart3 size={20} />
          Results
        </button>
      </div>

      {/* Tab Content */}
      <div className="scanner-content">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'results' && renderResults()}
      </div>

      {/* Scan Detail Modal */}
      {renderDetailModal()}

      {/* Loading overlay */}
      {loadingDetail && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading scan details...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AzureDevOpsScanner;
